import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  IconBell,
  IconBrandDiscord,
  IconBuildingStore,
  IconClockHour4,
  IconConfetti,
  IconHelpCircle,
  IconLayoutDashboard,
  IconMail,
  IconMessageCircle,
  IconPaperclip,
  IconSearch,
  IconSelector,
  IconSettings,
  IconWallet,
  IconX,
} from "@tabler/icons-react";

import FolderMetaIcon from "../../assets/folder-meta.svg?react";
import { useAuth } from "../../hooks/useAuth";
import { useAffiliate } from "../../contexts/affiliate";
import { usePortalScope } from "@/contexts/portalScope";
import type { PortalBrandRef } from "@/contexts/portalScope/context";
import { usePortalBranding } from "@/contexts/portalBranding";
import { programPageService, type ProgramPageNavItem } from "@/services/programPage.service";
import { LucideByName } from "@/components/programPage/icons";
import { UserAvatar } from "../common/UserAvatar";
import { PoweredByReveshare } from "../common/PoweredByReveshare";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  DarkModeRow,
  EdgeToggle,
  ICON,
  MenuRow,
  NavRow,
  RowSkeleton,
  SUB_ICON,
  SectionHeading,
  SignOutRow,
  SubList,
  SubRow,
} from "@/components/navigation/sidebar-kit";
import logo from "../../assets/logo.svg";

/* The affiliate sidebar on the same language as the brand one: Tabler icons,
   a rail on the active row, sections in sentence case, one edge toggle, and
   the account at the foot. Element ids are the onboarding tour's anchors and
   must not change. */

interface NavSubItem {
  label: string;
  id: string;
  path: string;
  icon: React.ReactNode;
  /** Marketplace-only entry: hidden for affiliates scoped to one brand. */
  marketplaceOnly?: boolean;
  /**
   * Only meaningful with more than one brand. A scoped affiliate has exactly
   * one, so a list of "active brands" is a one-row table pointing at the brand
   * whose portal they are already inside.
   */
  multiBrandOnly?: boolean;
}

interface NavItem {
  label: string;
  id: string;
  path: string;
  /** Route prefix that keeps the row highlighted; defaults to `path`. */
  activePrefix?: string;
  icon: React.ReactNode;
  marketplaceOnly?: boolean;
  /**
   * Names other brands or the wider platform (the cross-brand rewards
   * leaderboard). Hidden fail-closed, including while the scope loads.
   */
  crossBrandOnly?: boolean;
  multiBrandOnly?: boolean;
  subItems?: NavSubItem[];
}

interface NavSection {
  id: string;
  /** Omitted for the top group, which needs no heading. */
  label?: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    id: "overview",
    items: [
      {
        label: "Dashboard",
        id: "home",
        path: "/affiliate/dashboard",
        icon: <IconLayoutDashboard {...ICON} />,
      },
    ],
  },
  {
    id: "earn",
    label: "Earn",
    items: [
      {
        label: "Brands",
        id: "brands",
        path: "/affiliate/brands",
        icon: <IconBuildingStore {...ICON} />,
        subItems: [
          {
            multiBrandOnly: true,
            label: "Active brands",
            id: "active-brands",
            path: "/affiliate/brands",
            icon: <IconBuildingStore {...SUB_ICON} />,
          },
          {
            label: "Brand resources",
            id: "brand-resources",
            path: "/affiliate/brand-resources",
            icon: <IconPaperclip {...SUB_ICON} />,
          },
          {
            label: "Find brands",
            id: "search-brands",
            path: "/affiliate/brands/search",
            icon: <IconSearch {...SUB_ICON} />,
            marketplaceOnly: true,
          },
        ],
      },
      {
        label: "Content programs",
        id: "content-programs",
        path: "/affiliate/content-programs",
        icon: <FolderMetaIcon className="size-5" />,
      },
      {
        label: "Milestone rewards",
        id: "rewards",
        path: "/affiliate/rewards",
        icon: <IconConfetti {...ICON} />,
        crossBrandOnly: true,
      },
      {
        // At most one invitation for a scoped affiliate, shown as a dashboard
        // alert instead of earning a page of its own.
        multiBrandOnly: true,
        label: "Invitations",
        id: "invitations",
        path: "/affiliate/invitations",
        icon: <IconMail {...ICON} />,
      },
    ],
  },
  {
    id: "money",
    label: "Money",
    items: [
      {
        label: "Payouts",
        id: "payouts",
        path: "/affiliate/payouts",
        icon: <IconWallet {...ICON} />,
        subItems: [
          {
            label: "Request payout",
            id: "request-payout",
            path: "/affiliate/payouts",
            icon: <IconWallet {...SUB_ICON} />,
          },
          {
            label: "Payout history",
            id: "payout-history",
            path: "/affiliate/payouts/history",
            icon: <IconClockHour4 {...SUB_ICON} />,
          },
        ],
      },
    ],
  },
  {
    id: "you",
    label: "You",
    items: [
      {
        label: "Notifications",
        id: "notifications",
        path: "/affiliate/notifications",
        icon: <IconBell {...ICON} />,
      },
      {
        label: "Settings",
        id: "settings",
        path: "/affiliate/settings",
        icon: <IconSettings {...ICON} />,
      },
    ],
  },
];

/** Exact for leaf routes; prefix for anything with detail pages under it. */
function routeMatches(pathname: string, base: string) {
  if (base === "/affiliate/dashboard") {
    return pathname === "/affiliate" || pathname === "/affiliate/" || pathname.startsWith(base);
  }
  return pathname === base || pathname.startsWith(base + "/");
}

/**
 * Drops every `marketplaceOnly` entry for affiliates scoped to a single brand,
 * so a white-label portal never offers the wider Reveshare marketplace, and
 * every `crossBrandOnly` entry while cross-brand surfaces are hidden (which
 * includes the loading beat — fail closed).
 */
function visibleSections(
  isMarketplaceHidden: boolean,
  isCrossBrandHidden: boolean,
  portalBrand: PortalBrandRef | null,
  programPage: ProgramPageNavItem | null = null
): NavSection[] {
  // Confined to one brand — by the portal host or by the affiliate's scope.
  const isScoped = portalBrand !== null;
  const keep = (item: { marketplaceOnly?: boolean; crossBrandOnly?: boolean; multiBrandOnly?: boolean }) =>
    !(isMarketplaceHidden && item.marketplaceOnly) &&
    !(isCrossBrandHidden && item.crossBrandOnly) &&
    !(isScoped && item.multiBrandOnly);

  const sections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(keep).map((item) => {
      const next: NavItem = item.subItems
        ? { ...item, subItems: item.subItems.filter(keep) }
        : { ...item };
      // "Brands" is the wrong word for an affiliate who has exactly one. Point
      // the group at that brand and name it after them, so the sidebar reads
      // as the brand's own portal rather than a directory with one entry.
      if (next.id === "brands" && portalBrand?.username) {
        next.label = portalBrand.name || "Your brand";
        next.path = `/affiliate/brands/${portalBrand.username}`;
        next.activePrefix = "/affiliate/brands";
      }
      return next;
    }),
  })).filter((section) => section.items.length > 0);

  // The portal brand's own tier page, under the label and icon it chose.
  // Right under the dashboard: it is the brand's pitch for why to keep selling.
  if (programPage) {
    sections[0] = {
      ...sections[0],
      items: [
        ...sections[0].items,
        {
          label: programPage.label,
          id: "program-page",
          path: `/affiliate/program/${programPage.brandUsername}`,
          icon: <LucideByName name={programPage.icon} className="size-5" />,
        },
      ],
    };
  }
  return sections;
}

/* ------------------------------------------------------------------ */

function NavGroup({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const activeSub = item.subItems?.find((s) => routeMatches(location.pathname, s.path));
  const isActive = routeMatches(location.pathname, item.activePrefix ?? item.path) || !!activeSub;
  const hasSubItems = !!item.subItems && item.subItems.length > 0;
  const [expanded, setExpanded] = useState(isActive);
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  useEffect(() => {
    setExpanded(isActive);
  }, [isActive]);

  if (!hasSubItems) {
    return (
      <div id={item.id} className="relative">
        <NavRow
          to={item.path}
          label={item.label}
          icon={item.icon}
          active={isActive}
          collapsed={collapsed}
        />
      </div>
    );
  }

  // Collapsed: icon-only rows, with a flyout so child pages stay reachable.
  if (collapsed) {
    return (
      <div id={item.id} className="relative">
        <Popover open={flyoutOpen} onOpenChange={setFlyoutOpen}>
          <PopoverTrigger asChild>
            <span onMouseEnter={() => setFlyoutOpen(true)} onMouseLeave={() => setFlyoutOpen(false)}>
              <NavRow to={item.path} label={item.label} icon={item.icon} active={isActive} collapsed />
            </span>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={12}
            className="w-56 !p-1.5"
            onMouseEnter={() => setFlyoutOpen(true)}
            onMouseLeave={() => setFlyoutOpen(false)}
          >
            <SubRow
              to={item.path}
              label={item.label}
              active={isActive && !activeSub}
              onClick={() => setFlyoutOpen(false)}
            />
            <div className="mt-1 space-y-0.5 border-t border-border pt-1">
              {item.subItems!.map((sub) => (
                <SubRow
                  key={sub.id}
                  id={sub.id}
                  to={sub.path}
                  label={sub.label}
                  icon={sub.icon}
                  active={activeSub?.id === sub.id}
                  onClick={() => setFlyoutOpen(false)}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div id={item.id} className="relative">
      <NavRow
        to={item.path}
        label={item.label}
        icon={item.icon}
        active={isActive}
        collapsed={false}
        expandable
        expanded={expanded}
        onToggleExpand={() => setExpanded((v) => !v)}
      />
      {expanded && (
        <SubList>
          {item.subItems!.map((sub) => (
            <SubRow
              key={sub.id}
              id={sub.id}
              to={sub.path}
              label={sub.label}
              icon={sub.icon}
              active={activeSub?.id === sub.id}
            />
          ))}
        </SubList>
      )}
    </div>
  );
}

/** Logo and name: the white-label brand's, or Reveshare's. Collapsed shows
    the mark alone, with an initial when a brand has no logo yet. */
function PortalHeader({ collapsed }: { collapsed: boolean }) {
  const { isWhiteLabel, portalName, portalLogo, isReady } = usePortalBranding();
  if (!isReady) return <div className={cn(collapsed ? "h-[60px]" : "h-[68px]")} />;

  const mark = isWhiteLabel ? (
    portalLogo ? (
      <img src={portalLogo} alt={portalName} className="size-9 rounded-lg object-contain" />
    ) : (
      <span className="flex size-9 items-center justify-center rounded-lg bg-brand-600 text-sm font-semibold text-brand-contrast">
        {portalName.trim().charAt(0).toUpperCase()}
      </span>
    )
  ) : (
    <img src={logo} alt="Reveshare" className="size-9 dark:invert" />
  );

  return (
    <div
      className={cn(
        "flex items-center",
        collapsed ? "justify-center px-3.5 pt-4 pb-2" : "gap-2.5 px-4 pt-4 pb-3"
      )}
    >
      {mark}
      {!collapsed && (
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold leading-tight text-foreground">
            {isWhiteLabel ? portalName : "Reveshare"}
          </div>
          <PoweredByReveshare className="justify-start py-0 pt-0.5 text-left" />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface SidebarProps {
  /** Mobile overlay state; the layout owns the open button. */
  isOpen: boolean;
  onClose: () => void;
  /** Opens the Reveshare Discord invite. Hidden inside a white-label portal. */
  onOpenDiscord?: () => void;
}

// Tablet widths start collapsed; the person's own toggle wins after that.
const initialCollapsed = () => {
  const width = window.innerWidth;
  return width >= 768 && width < 980;
};

export function Sidebar({ isOpen, onClose, onOpenDiscord }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { details } = useAffiliate();
  const { isMarketplaceHidden, isCrossBrandHidden, portalBrand, isLoading: scopeLoading } =
    usePortalScope();
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isManuallyToggled, setIsManuallyToggled] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // Only a session confined to one brand has a program page to link to; the
  // open marketplace never asks.
  const { data: programPageNav } = useQuery({
    queryKey: ["program-page", "nav", portalBrand?.id],
    queryFn: programPageService.getAffiliateNav,
    enabled: portalBrand !== null,
    staleTime: 5 * 60 * 1000,
  });
  const programPage = (portalBrand && programPageNav?.item) || null;
  const sections = useMemo(
    () => visibleSections(isMarketplaceHidden, isCrossBrandHidden, portalBrand, programPage),
    [isMarketplaceHidden, isCrossBrandHidden, portalBrand, programPage]
  );

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      if (width < 768) setIsCollapsed(false);
      else if (!isManuallyToggled) setIsCollapsed(width < 980);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isManuallyToggled]);

  // The mobile overlay closes on navigation and locks the page behind it.
  useEffect(() => {
    if (isMobile && isOpen) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  useEffect(() => {
    if (!(isMobile && isOpen)) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobile, isOpen]);

  const name =
    [details?.firstName ?? user?.firstName, details?.lastName ?? user?.lastName]
      .filter(Boolean)
      .join(" ") || "Your account";
  const email = details?.email || user?.email || "";

  const handleLogout = () => {
    setAccountOpen(false);
    logout();
    navigate("/auth/login");
  };

  const isAccountPage = ["/affiliate/faq", "/affiliate/support"].some((p) =>
    routeMatches(location.pathname, p)
  );

  const nav = (collapsed: boolean) => (
    <nav className={cn("py-3", collapsed ? "px-3.5" : "px-2")}>
      {scopeLoading
        ? Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} collapsed={collapsed} />)
        : sections.map((section, index) => (
            <div key={section.id} className={cn(index > 0 && "mt-4")}>
              {index > 0 && section.label && (
                <SectionHeading label={section.label} collapsed={collapsed} />
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavGroup key={item.id} item={item} collapsed={collapsed} />
                ))}
              </div>
            </div>
          ))}
    </nav>
  );

  const account = (collapsed: boolean, side: "top" | "right") => (
    <Popover open={accountOpen} onOpenChange={setAccountOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Account"
          aria-label="Account"
          className={cn(
            "flex items-center rounded-md transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            collapsed ? "mx-auto size-10 justify-center" : "w-full gap-2.5 p-1.5 text-left"
          )}
        >
          <span
            className={cn(
              "flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted",
              isAccountPage && "ring-2 ring-brand-600 ring-offset-2 ring-offset-card"
            )}
          >
            <UserAvatar src={details?.profileImage} name={name} className="h-full w-full text-sm" />
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium text-foreground">{name}</span>
                <span className="block truncate text-[11px] text-muted-foreground">{email}</span>
              </span>
              <IconSelector size={16} stroke={1.75} className="shrink-0 text-muted-foreground" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={side === "top" ? "start" : "end"}
        sideOffset={10}
        className="w-60 !p-1.5"
      >
        <div className="space-y-0.5">
          {collapsed && (
            <div className="mb-1 border-b border-border px-2 pb-2 pt-1">
              <div className="truncate text-sm font-medium text-foreground">{name}</div>
              <div className="truncate text-xs text-muted-foreground">{email}</div>
            </div>
          )}
          {!isMarketplaceHidden && (
            <MenuRow
              id="faq"
              icon={<IconHelpCircle />}
              label="FAQ"
              active={routeMatches(location.pathname, "/affiliate/faq")}
              onClick={() => {
                setAccountOpen(false);
                navigate("/affiliate/faq");
              }}
            />
          )}
          <MenuRow
            icon={<IconMessageCircle />}
            label="Support"
            active={routeMatches(location.pathname, "/affiliate/support")}
            onClick={() => {
              setAccountOpen(false);
              navigate("/affiliate/support");
            }}
          />
          {onOpenDiscord && !isCrossBrandHidden && (
            <MenuRow
              icon={<IconBrandDiscord />}
              label="Join the Discord"
              onClick={() => {
                setAccountOpen(false);
                onOpenDiscord();
              }}
            />
          )}
          <DarkModeRow />
          <SignOutRow onClick={handleLogout} />
        </div>
      </PopoverContent>
    </Popover>
  );

  if (isMobile) {
    return (
      <div className="no-print">
        {isOpen && <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />}
        <aside
          id="mobile-sidebar"
          className={cn(
            "fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r border-border bg-card transition-transform duration-300",
            isOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex items-start justify-between pr-2">
            <PortalHeader collapsed={false} />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              className="mt-4 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <IconX size={18} stroke={1.75} />
            </button>
          </div>
          <div className="scrollbar-hide flex-1 overflow-y-auto">{nav(false)}</div>
          <div className="border-t border-border p-2">{account(false, "top")}</div>
        </aside>
      </div>
    );
  }

  return (
    <aside
      className={cn(
        "no-print group/sidebar sticky top-0 left-0 z-30 flex h-screen flex-col border-r border-border bg-card transition-[width] duration-300",
        isCollapsed ? "w-[68px]" : "w-64"
      )}
      onMouseEnter={() => {
        if (window.innerWidth < 980 && !isManuallyToggled) setIsCollapsed(false);
      }}
      onMouseLeave={() => {
        if (window.innerWidth < 980 && !isManuallyToggled) setIsCollapsed(true);
      }}
    >
      <EdgeToggle
        collapsed={isCollapsed}
        onToggle={() => {
          setIsCollapsed((v) => !v);
          setIsManuallyToggled(true);
        }}
      />
      <PortalHeader collapsed={isCollapsed} />
      <div className="scrollbar-hide flex-1 overflow-y-auto overflow-x-hidden">{nav(isCollapsed)}</div>
      <div className={cn("border-t border-border", isCollapsed ? "px-3.5 py-3" : "p-2")}>
        {account(isCollapsed, isCollapsed ? "right" : "top")}
      </div>
    </aside>
  );
}
